import React, { useEffect, useState } from 'react'
import { useFormContext, useFieldArray } from 'react-hook-form'
import { SelectInput, useTranslate, useNotify, required, FormDataConsumer, BooleanInput } from 'react-admin'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { Delete, Add } from '@mui/icons-material'
import Button from '@mui/material/Button'
import { Grid, Skeleton } from '@mui/material'
import { ConfirmationDialog } from 'src/components/uiElements/styledComponents/ConfirmationDialog'
import { bannerServices } from 'src/services/reservation/banner/Banner.service'
import { removeBannersByIds } from '../../helper'
import { AutocompleteInput } from 'src/components/uiElements/fields/AutocompleteInput'

export const FieldArrayBanner = ({ editMode = true }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const { control } = useFormContext()
  const [banners, setBanners] = useState([])
  const [loading, setLoading] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(null)
  const { fields, append, remove, update } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: 'banner_relation', // unique name for your Field Array
    keyName: '_id',
  })

  useEffect(() => {
    ;(async () => {
      await getAllBanners()
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (confirmDelete) {
      removeBannersByIds(currentIndex, fields, remove, editMode, notify, true)
      setOpenModal(false)
      setCurrentIndex(null)
      setConfirmDelete(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmDelete])

  const getAllBanners = async () => {
    setLoading(true)
    try {
      const allBanners = await bannerServices.getByAll({ limit: 999 })
      if (allBanners) {
        setBanners(allBanners)
        setLoading(false)
        return
      }
      setBanners([])
      notify(`Banners not found`, { type: 'error' })
      setLoading(false)
    } catch (error) {
      setBanners([])
      notify(`please, refresh page`, { type: 'error' })
      setLoading(false)
    }
  }

  const updateBanner = (index, field, value) => {
    const data = {
      ...fields[index],
      [field]: value,
      update_register: true
    }
    update(index, data)
  }

  const deleteBanner = (originalIndex, banner, remove) => {
    if (editMode && !('new_register' in banner)) {
      setOpenModal(true)
      setCurrentIndex(originalIndex)
    } else {
      remove(originalIndex)
    }
  }

  return (
    <>
      <ConfirmationDialog
        title={translate('ra.message.delete_banner')}
        buttonConfirmText={translate('ra.action.to_confirm')}
        buttonCancel={translate('ra.action.cancel')}
        setOpen={setOpenModal}
        setConfirm={setConfirmDelete}
        open={openModal}
      />
      {React.Children.toArray(
        fields.map((field, index) => (
          <div
            key={field._id}
            style={{
              width: '100%',
              height: 'auto',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: 10
            }}
          >
            <Grid container spacing={3}>
              <EditInputsWrap label={translate('ra.field.banner')} required md={6} className='small'>
                {loading ? (
                  <Skeleton variant='rectangular' width={'100%'} height={40} />
                ) : (
                  <FormDataConsumer>
                    {({ formData }) => {
                      const options = banners.filter(elem => {
                        if (formData.banner_relation[index].banner_relation?.id === elem.id) {
                          return true
                        }

                        return !formData.banner_relation.find(product => product.banner_relation?.id === elem.id)
                      })

                      return <AutocompleteInput
                        source={`banner_relation.${index}.banner_relation`}
                        options={editMode && !('new_register' in field) ? banners : options}
                        optionsText={['short_name']}
                        fullWidth
                        validate={required()}
                        disabled={editMode && !('new_register' in field)}
                      />
                    }}
                  </FormDataConsumer>
                )}
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.is_active')} md={1} className='small'>
                <BooleanInput
                  defaultValue={true}
                  source={`banner_relation.${index}.is_active`}
                  label={false}
                  fullWidth
                  onChange={e => updateBanner(index, 'is_active', e.target.checked)}
                />
              </EditInputsWrap>
            </Grid>
            <Grid container spacing={3} justifyContent='flex-end' className='addItemSeparator'>
              <Grid item xs={12} sm={12} className='grid' style={{ marginTop: 10 }}>
                <Button
                  onClick={() => deleteBanner(index, field, remove)}
                  startIcon={<Delete />}
                  color='error'
                >
                  {translate('ra.action.delete')}
                </Button>
              </Grid>
            </Grid>
          </div>
        ))
      )}
      <Button
        startIcon={<Add />}
        onClick={() => {
          append({ banner_id: '', is_active: true, new_register: true })
        }}
      >
        {translate('ra.action.add_specific', {
          name: 'Banner',
        })}
      </Button>
    </>
  )
}
